import './Home.scss';
import {
    Box,
    Card,
    CardContent,
    Container,
    Paper,
    Tab,
    Tabs,
    Typography,
    Avatar,
    Theme
} from '@mui/material';
import {FullStack} from "./FullStack";
import {Basics} from "./Basics";
import {AdvanceML} from "./AdvanceML";
import { useState } from 'react';

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ p: 3 }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

export const Home = () => {
    const [value, setValue] = useState(0);

    const handleChange = (event: React.SyntheticEvent, newValue: number) => {
        setValue(newValue);
    };

    // Decorative background graphics - very subtle
    const BackgroundGraphics = () => (
        <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', overflow: 'hidden', zIndex: 0, pointerEvents: 'none', opacity: 0.3 }}>
            <svg width="100%" height="100%" viewBox="0 0 1200 600" preserveAspectRatio="xMidYMid slice">
                <defs>
                    <linearGradient id="homeGrad1" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#667eea" stopOpacity="0.03" />
                        <stop offset="100%" stopColor="#764ba2" stopOpacity="0.03" />
                    </linearGradient>
                    <linearGradient id="homeGrad2" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#f093fb" stopOpacity="0.03" />
                        <stop offset="100%" stopColor="#4facfe" stopOpacity="0.03" />
                    </linearGradient>
                </defs>
                {/* Very subtle floating shapes */}
                <circle cx="100" cy="100" r="80" fill="url(#homeGrad1)" opacity="0.2" />
                <circle cx="1100" cy="150" r="100" fill="url(#homeGrad2)" opacity="0.2" />
                <circle cx="600" cy="50" r="60" fill="url(#homeGrad1)" opacity="0.15" />
                <circle cx="200" cy="400" r="70" fill="url(#homeGrad2)" opacity="0.15" />
                <circle cx="1000" cy="450" r="90" fill="url(#homeGrad1)" opacity="0.2" />
            </svg>
        </Box>
    );

    return (
        <Box sx={{ position: 'relative' }}>
            <BackgroundGraphics />
            <Container 
                maxWidth="lg"
                sx={{ 
                    px: { xs: 1, sm: 2 },
                    position: 'relative',
                    zIndex: 2,
                    backgroundColor: 'transparent'
                }}
            >
                <Box sx={{ position: 'relative', mb: { xs: 3, sm: 4 } }}>
                    <Box sx={{ position: 'absolute', top: -50, left: '50%', transform: 'translateX(-50%)', width: '100%', maxWidth: 600, height: 100, opacity: 0.2, zIndex: 0 }}>
                        <svg viewBox="0 0 600 100" preserveAspectRatio="xMidYMid meet">
                            <defs>
                                <linearGradient id="titleGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                                    <stop offset="0%" stopColor="#667eea" stopOpacity="0.3" />
                                    <stop offset="50%" stopColor="#f093fb" stopOpacity="0.3" />
                                    <stop offset="100%" stopColor="#4facfe" stopOpacity="0.3" />
                                </linearGradient>
                            </defs>
                            <ellipse cx="300" cy="50" rx="250" ry="40" fill="url(#titleGrad)" />
                        </svg>
                    </Box>
                    <Typography 
                        variant="h3" 
                        component="h1" 
                        gutterBottom 
                        align="center" 
                        sx={{ 
                            mb: { xs: 2, sm: 3, md: 4 },
                            fontSize: { xs: '1.75rem', sm: '2.25rem', md: '3rem' },
                            position: 'relative',
                            zIndex: 1,
                            fontWeight: 'bold',
                            background: (theme: Theme) => 
                                theme.palette.mode === 'dark' 
                                    ? 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)'
                                    : 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
                            backgroundClip: 'text',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                        }}
                    >
                        Welcome, this is Meeraj Kanaparthi
                    </Typography>
                </Box>
            
            <Box 
                sx={{ 
                    display: 'flex', 
                    flexDirection: { xs: 'column', md: 'row' }, 
                    gap: { xs: 2, sm: 3 }, 
                    mb: { xs: 2, sm: 3, md: 4 }
                }}
            >
                <Box 
                    sx={{ 
                        flex: { xs: '1', md: '0 0 33%' }, 
                        display: 'flex', 
                        justifyContent: 'center',
                        mb: { xs: 2, md: 0 },
                        position: 'relative'
                    }}
                >
                    <Box sx={{ position: 'relative', display: 'inline-block' }}>
                        {/* Decorative ring around avatar */}
                        <Box sx={{ 
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: { xs: 140, sm: 170, md: 220 },
                            height: { xs: 140, sm: 170, md: 220 },
                            borderRadius: '50%',
                            border: '4px solid',
                            borderColor: (theme: Theme) => 
                                theme.palette.mode === 'dark' 
                                    ? 'rgba(102, 126, 234, 0.3)' 
                                    : 'rgba(102, 126, 234, 0.2)',
                            zIndex: 0,
                            animation: 'pulse 3s ease-in-out infinite',
                            '@keyframes pulse': {
                                '0%, 100%': { transform: 'translate(-50%, -50%) scale(1)', opacity: 0.5 },
                                '50%': { transform: 'translate(-50%, -50%) scale(1.1)', opacity: 0.8 }
                            }
                        }} />
                        <Avatar
                            src="https://miro.medium.com/fit/c/262/262/1*Rr6FjqrY5bsuuhHv-QzcBg.jpeg"
                            sx={{ 
                                width: { xs: 120, sm: 150, md: 200 }, 
                                height: { xs: 120, sm: 150, md: 200 },
                                position: 'relative',
                                zIndex: 1,
                                border: '4px solid',
                                borderColor: (theme: Theme) => 
                                    theme.palette.mode === 'dark' 
                                        ? 'rgba(118, 75, 162, 0.5)' 
                                        : 'rgba(118, 75, 162, 0.3)',
                                boxShadow: (theme: Theme) => 
                                    theme.palette.mode === 'dark' 
                                        ? '0 8px 32px rgba(102, 126, 234, 0.3)' 
                                        : '0 8px 32px rgba(102, 126, 234, 0.2)'
                            }}
                        />
                    </Box>
                </Box>
                <Box 
                    sx={{ 
                        flex: { xs: '1', md: '0 0 67%' },
                        textAlign: { xs: 'center', md: 'left' }
                    }}
                >
                    <Paper 
                        elevation={2} 
                        sx={{ 
                            p: { xs: 2, sm: 3 },
                            borderRadius: { xs: 1, sm: 2 }
                        }}
                    >
                        <Typography 
                            variant='body1' 
                            paragraph
                            sx={{ 
                                fontSize: { xs: '0.9rem', sm: '1rem' },
                                mb: { xs: 2, sm: 3 }
                            }}
                        >
                            This is my hobby to teach and share knowledge in NLP and ML. I have created content, blogs, videos along with the code.
                        </Typography>
                        <Typography 
                            variant='body1' 
                            paragraph
                            sx={{ 
                                fontSize: { xs: '0.9rem', sm: '1rem' },
                                mb: { xs: 2, sm: 3 }
                            }}
                        >
                            For few videos I have created Hindi and Telugu versions as well.
                        </Typography>
                        <Typography 
                            variant='body1'
                            sx={{ 
                                fontSize: { xs: '0.9rem', sm: '1rem' }
                            }}
                        >
                            For Contact details click on the bottom of the page.
                        </Typography>
                    </Paper>
                </Box>
            </Box>

            <Card 
                sx={{ 
                    mb: { xs: 2, sm: 3 },
                    boxShadow: { xs: 1, sm: 2 },
                    borderRadius: { xs: 1, sm: 2 },
                    position: 'relative',
                    overflow: 'hidden',
                    background: (theme: Theme) => 
                        theme.palette.mode === 'dark' 
                            ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)'
                            : 'linear-gradient(135deg, rgba(102, 126, 234, 0.02) 0%, rgba(118, 75, 162, 0.02) 100%)',
                }}
            >
                <Box sx={{ position: 'absolute', top: 10, right: 10, width: 100, height: 100, opacity: 0.1, zIndex: 0 }}>
                    <svg viewBox="0 0 100 100">
                        <polygon points="50,10 90,50 50,90 10,50" fill="currentColor" />
                        <circle cx="50" cy="50" r="20" fill="none" stroke="currentColor" strokeWidth="2" />
                    </svg>
                </Box>
                <CardContent sx={{ position: 'relative', zIndex: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <iframe 
                            width="560" 
                            height="315" 
                            src="https://www.youtube.com/embed/iJls49fNg38" 
                            frameBorder="0"
                            allowFullScreen
                            title="YouTube video"
                            style={{ borderRadius: '8px' }}
                        />
                    </Box>
                </CardContent>
            </Card>

            <Paper 
                sx={{ 
                    width: '100%',
                    boxShadow: { xs: 1, sm: 2 },
                    borderRadius: { xs: 1, sm: 2 },
                    position: 'relative',
                    overflow: 'hidden',
                    background: (theme: Theme) => 
                        theme.palette.mode === 'dark' 
                            ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.03) 0%, rgba(118, 75, 162, 0.03) 100%)'
                            : 'linear-gradient(135deg, rgba(102, 126, 234, 0.01) 0%, rgba(118, 75, 162, 0.01) 100%)',
                }}
            >
                <Box sx={{ position: 'absolute', bottom: 0, left: 0, width: '100%', height: '2px', 
                    background: 'linear-gradient(90deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
                    opacity: 0.5
                }} />
                <Box sx={{ borderBottom: 1, borderColor: 'divider', position: 'relative' }}>
                    <Tabs 
                        value={value} 
                        onChange={handleChange} 
                        aria-label="content tabs"
                        variant="scrollable"
                        scrollButtons="auto"
                        sx={{ 
                            '& .MuiTab-root': {
                                fontSize: { xs: '0.8rem', sm: '0.875rem', md: '1rem' },
                                minWidth: { xs: 'auto', sm: '120px' },
                                px: { xs: 1, sm: 2 },
                                fontWeight: 500
                            },
                            '& .Mui-selected': {
                                color: (theme: Theme) => theme.palette.mode === 'dark' ? '#667eea' : '#764ba2'
                            }
                        }}
                    >
                        <Tab label="FullStack" />
                        <Tab label="Advance" />
                        <Tab label="Basics" />
                    </Tabs>
                </Box>
                <TabPanel value={value} index={0}>
                    <FullStack />
                </TabPanel>
                <TabPanel value={value} index={1}>
                    <AdvanceML />
                </TabPanel>
                <TabPanel value={value} index={2}>
                    <Basics />
                </TabPanel>
            </Paper>
        </Container>
        </Box>
    );
}